import React, { useState, useEffect } from "react";
import { Users, UserPlus, Trash2, Mail, Lock, ShieldCheck, Activity } from "lucide-react";
import { db } from "../../firebase/config";
import { secondaryAuth } from "../../firebase/secondary_auth";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { ref, set, push, onValue, remove } from "firebase/database";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { generateClinicalId } from "../../utils/idGenerator";

const CaregiverManager = () => {
  const { currentUser, userData } = useAuth();
  const { showAlert } = useToast();
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [newCaregiver, setNewCaregiver] = useState({
    name: "",
    email: "",
    password: ""
  });

  useEffect(() => {
    if (!currentUser) return;

    // Synchronize linked caregiver network
    const caregiversRef = ref(db, "users");
    const unsubscribe = onValue(caregiversRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data)
          .filter(([uid, val]) => val.role === "caregiver" && val.linkedPatientId === currentUser.uid)
          .map(([uid, val]) => ({ uid, ...val }));
        setCaregivers(list);
      } else {
        setCaregivers([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleAddCaregiver = async (e) => {
    e.preventDefault();
    if (!newCaregiver.email || !newCaregiver.password || !newCaregiver.name) {
      showAlert("PROTOCOL_ERROR: Complete medical metadata required for caregiver provisioning.", "error");
      return;
    }

    setLoading(true);
    try {
      // 1. Provision Unique Caregiver Identity
      const caregiverId = await generateClinicalId("caregiver");

      // 2. Background Auth Provisioning (Secondary App Instance)
      // This prevents the current Patient from being signed out
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        newCaregiver.email,
        newCaregiver.password
      );
      
      const cgUser = userCredential.user;

      // 3. Seed Clinical Metadata & Linkage (Decentralized Architecture)
      const caregiverProfile = {
        uid: cgUser.uid,
        customId: caregiverId,
        name: newCaregiver.name,
        email: newCaregiver.email,
        role: "caregiver",
        linkedPatientId: currentUser.uid,
        patientName: userData?.name || "Patient",
        status: "active",
        createdAt: new Date().toISOString()
      };

      // Atomic Provisioning: Caregiver Node + Patient Linkage
      const updates = {};
      updates[`caregivers/${cgUser.uid}`] = caregiverProfile;
      updates[`users/${currentUser.uid}/caregivers/${cgUser.uid}`] = true;

      const { update } = await import("firebase/database");
      await update(ref(db), updates);

      // 4. Clean up secondary auth session immediately
      await signOut(secondaryAuth);

      showAlert(`CAREGIVER_PROVISIONED: ${caregiverId} successfully linked to patient node.`, "success");
      setNewCaregiver({ name: "", email: "", password: "" });
    } catch (error) {
      console.error("CAREGIVER_PROVISIONING_FAILURE:", error);
      showAlert("PROVISIONING_FAILURE: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const unlinkCaregiver = async (cgUid) => {
    if (window.confirm("CONFIRM_ACTION: Revoke clinical access for this caregiver?")) {
      try {
        // We don't delete the auth user (security policy), just revoke the link and role
        await remove(ref(db, `users/${cgUid}`));
        showAlert("LINK_REVOKED: Caregiver access successfully terminated.", "success");
      } catch (error) {
        showAlert("REVOCATION_FAILURE: " + error.message, "error");
      }
    }
  };

  return (
    <div className="settings-card glass-card caregiver-manager-hud mt-8">
      <div className="card-header">
        <Users size={18} className="icon-blue" />
        <h3>Caregiver & Family Network</h3>
      </div>

      <div className="card-body">
        <form onSubmit={handleAddCaregiver} className="caregiver-provisioning-form glass-card p-6 mb-8" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <div className="hud-label-row mb-4">
            <UserPlus size={14} className="icon-blue" />
            <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>PROVISION_NEW_CAREGIVER</span>
          </div>

          <div className="profile-form-grid">
            <div className="input-group">
              <label>Full Name</label>
              <input 
                type="text" 
                placeholder="Caregiver Name" 
                value={newCaregiver.name}
                onChange={(e) => setNewCaregiver({...newCaregiver, name: e.target.value})}
              />
            </div>
            <div className="input-group">
              <label>Email Identity</label>
              <input 
                type="email" 
                placeholder="caregiver@clinical.com" 
                value={newCaregiver.email}
                onChange={(e) => setNewCaregiver({...newCaregiver, email: e.target.value})}
              />
            </div>
            <div className="input-group full-width">
              <label>Secure Access Code (Password)</label>
              <input 
                type="password" 
                placeholder="********" 
                value={newCaregiver.password}
                onChange={(e) => setNewCaregiver({...newCaregiver, password: e.target.value})}
              />
            </div>
          </div>

          <button type="submit" className="premium-btn primary w-full mt-4" disabled={loading}>
            {loading ? "TRANSMITTING_METADATA..." : "AUTHORIZE_NEW_CAREGIVER"}
          </button>
        </form>

        <div className="caregiver-registry mt-8">
          <div className="hud-label-row mb-4">
            <ShieldCheck size={14} className="icon-green" />
            <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>ACTIVE_IDENTITY_LINKS</span>
          </div>

          <div className="caregiver-list" style={{ display: 'grid', gap: '1rem' }}>
            {caregivers.length > 0 ? caregivers.map((cg) => (
              <div key={cg.uid} className="caregiver-node glass-card p-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div className="avatar-mini" style={{ width: '40px', height: '40px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'var(--accent-blue)' }}>
                    {cg.name.charAt(0)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>{cg.name}</span>
                    <span style={{ fontSize: '0.7rem', fontFamily: 'Space Mono, monospace', color: 'var(--accent-cyan)' }}>CODE: {cg.customId}</span>
                  </div>
                </div>
                <button onClick={() => unlinkCaregiver(cg.uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-red)', opacity: 0.6 }}>
                  <Trash2 size={18} />
                </button>
              </div>
            )) : (
              <div className="empty-registry-placeholder p-8 text-center" style={{ border: '1px dashed var(--border-glass)', borderRadius: '1rem' }}>
                <Activity size={32} className="icon-dim pulse" />
                <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-dim)', fontFamily: 'Space Mono, monospace' }}>NO_AUTHORIZED_CAREGIVERS_DETECTED</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverManager;
